import React, { useState, useEffect, useRef, useMemo } from 'react';

// Components
import SearchableSelect from './components/SearchableSelect';
import FilterBuilderModal from './components/FilterBuilderModal';
import FieldPickerModal from './components/FieldPickerModal';
import CredentialsModal from './components/CredentialsModal';
import XmlPreviewModal from './components/XmlPreviewModal';
import UpdateConfirmationModal from './components/UpdateConfirmationModal';
import POCreateModal from './components/POCreateModal';
import JobCostSetupModal from './components/JobCostSetupModal';
import CostStructureModal from './components/CostStructureModal';
import ReadModePanel from './components/ReadModePanel';
import WriteModePanel from './components/WriteModePanel';
import PurchasingModePanel from './components/PurchasingModePanel';
import AttachmentsModePanel from './components/AttachmentsModePanel';
import ToolsModePanel from './components/ToolsModePanel';
import ReportsModePanel from './components/ReportsModePanel';
import ApiUsagePanel from './components/ApiUsagePanel';

// Icons
import { PaperclipIcon, DownloadIcon, RefreshIcon, CodeIcon } from './components/Icons';

// Utils & Constants
import { OBJECT_LIBRARY, SMART_DEFAULTS, WRITE_SUPPORTED_OBJECTS, CATEGORIES, HARDCODED_FIELDS } from './constants/objects';
import { PO_CONVERSION_CONFIG } from './constants/purchasingConfig';
import { transformPOData } from './utils/dateHelpers';
import { escapeXml, buildFilterPreview } from './utils/xmlHelpers';
import { extractErrorMessage, writeResultsToExcel, writeGroupedResultsToExcel } from './utils/excelResultWriter';
import { 
  fetchIntacctData, fetchMoreIntacctData, fetchIntacctDataLegacy, fetchIntacctDataByGet, buildGetPreviewXml,
  inspectIntacctObject, writeIntacctRecords, buildWritePreviewXml, getDefaultSavePath, browseFolderPath, downloadAttachmentsBatch,
  isRateTableEntryType, getRateTableUpdateSummary, buildRateTablePreviewXml, writeRateTableEntries,
  getRateTableCreateSummary, buildRateTableCreatePreviewXml, createRateTableEntries,
  isDocumentEntryType, getDocumentEntryConfig, resolveDocumentKeyHeader, getDocumentUpdateSummary, buildDocumentPreviewXml, writeDocumentEntries,
  isTransactionRuleType, getTransactionRuleCreateSummary, buildTransactionRulePreviewXml, createTransactionRules,
  isItemNestedEntryType, getItemNestedConfig, getItemNestedUpdateSummary, buildItemNestedPreviewXml, writeItemNestedEntries,
  isContractLineEntryType, detectContractLineParentKey, getContractLineUpdateSummary, buildContractLinePreviewXml, writeContractLineEntries,
  isVendorBankDetailType, getVendorBankSuggestedHeaders, getVendorBankCreateSummary, buildVendorBankPreviewXml, writeVendorBankDetails,
  getPOCreateSummary, buildPOCreatePreviewXml, createPOTransactions,
  isJobCostSetupFlow, getJobCostSetupSummary, buildJobCostPreviewXml, executeJobCostSetup,
  isAppPaymentCreate, getAppPaymentCreateSummary, buildAppPaymentPreviewXml, createAppPayments,
  detectCostStructureHeaders, extractCostStructureRows, validateCostStructure, createCostStructureTasks, createCostStructureCostTypes
} from './utils/api';

const ShoppingCartIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const LightningIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const WrenchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const App = () => {
  // Connection State
const [config, setConfig] = useState({ senderId: 'default', senderPassword: 'default', userId: '', companyId: '', userPassword: '' });
  
  // Mode & UI State
  const [mode, setMode] = useState('read');
  const [category, setCategory] = useState('All');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [isInspecting, setIsInspecting] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  
  // Query State
  const [queryObj, setQueryObj] = useState('VENDOR');
  const [selectedFields, setSelectedFields] = useState(['RECORDNO', 'VENDORID', 'NAME']);
  const [availableFields, setAvailableFields] = useState([]);
  const [sqlFilter, setSqlFilter] = useState("RECORDNO > 0");
  const [useLabelHeaders, setUseLabelHeaders] = useState(true);
  const [pageSize] = useState(2000);
  const [showPrivate, setShowPrivate] = useState(false);
  const [orderBy, setOrderBy] = useState(null);
  
  // Legacy Query Mode State (for readByQuery)
  const [useLegacyQuery, setUseLegacyQuery] = useState(false);
  const [legacyQueryString, setLegacyQueryString] = useState('');
  
  // GET Mode State (for read/readByName)
  const [useGetMode, setUseGetMode] = useState(false);
  const [getExcelRef, setGetExcelRef] = useState('');
  const [getKeyType, setGetKeyType] = useState('name'); // 'name' or 'recordno'
  const [exportGetXml, setExportGetXml] = useState(false); // Export XML when running GET
  
  // Modal State
  const [isFieldPickerOpen, setIsFieldPickerOpen] = useState(false);
  const [isFilterBuilderOpen, setIsFilterBuilderOpen] = useState(false);
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  const [isXmlPreviewOpen, setIsXmlPreviewOpen] = useState(false);
  const [xmlContent, setXmlContent] = useState('');
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [pendingDownload, setPendingDownload] = useState(null);
  
  // Attachments State
  const [attachmentIds, setAttachmentIds] = useState('');
  const [attachmentSource, setAttachmentSource] = useState('manual');
  const [attachmentExcelRef, setAttachmentExcelRef] = useState('');
  const [savePath, setSavePath] = useState('');
  const [attachmentResults, setAttachmentResults] = useState([]);
  
  // Purchasing State
  const [poFilter, setPoFilter] = useState("RECORDNO > 0");
  const [poAvailableFields, setPoAvailableFields] = useState([]);
  const [poOrderBy, setPoOrderBy] = useState(null);
  const [poShowPrivate, setPoShowPrivate] = useState(false);
  const [isPoFilterBuilderOpen, setIsPoFilterBuilderOpen] = useState(false);
  const [poCreateModalOpen, setPoCreateModalOpen] = useState(false);
  const [poCreateData, setPoCreateData] = useState(null);
  const [poExcludedFields, setPoExcludedFields] = useState([]);
  
  // Job Cost State
  const [jobCostModalOpen, setJobCostModalOpen] = useState(false);
  const [jobCostData, setJobCostData] = useState(null);
  const [jobCostResults, setJobCostResults] = useState(null);
  const [vendorBankHeadersModalOpen, setVendorBankHeadersModalOpen] = useState(false);

  // Cost Structure State
  const [costStructureMode, setCostStructureMode] = useState(false);
  const [costStructureModalOpen, setCostStructureModalOpen] = useState(false);
  const [costStructureRows, setCostStructureRows] = useState(null);
  const [costStructureValidation, setCostStructureValidation] = useState(null);
  const [costStructureTaskResults, setCostStructureTaskResults] = useState(null);
  const [costStructureCostTypeResults, setCostStructureCostTypeResults] = useState(null);
  const [costStructureBusyAction, setCostStructureBusyAction] = useState(null);
  
  const filterOriginSheet = useRef(null);
  const isFilterBuilderOpenRef = useRef(false);

  // ============ EFFECTS ============
  useEffect(() => { getDefaultSavePath().then(setSavePath).catch(console.error); }, []);
  
  useEffect(() => {
    if (typeof Office !== 'undefined' && typeof Excel !== 'undefined') {
      Office.onReady(() => {
        loadSettingsFromActiveSheet();
        Excel.run(async (context) => { context.workbook.worksheets.onActivated.add(loadSettingsFromActiveSheet); await context.sync(); }).catch(console.error);
      });
    }
  }, []);
  
  useEffect(() => { isFilterBuilderOpenRef.current = isFilterBuilderOpen || isPoFilterBuilderOpen; }, [isFilterBuilderOpen, isPoFilterBuilderOpen]);

  useEffect(() => {
    if (!queryObj || mode === 'purchasing') return;
    const fetchSchema = async () => {
      setIsInspecting(true); setStatus('');
      try {
        // Check for hardcoded fields first (objects that don't support inspect)
        const hardcoded = HARDCODED_FIELDS && HARDCODED_FIELDS[queryObj];
        if (hardcoded) {
          const fields = hardcoded.map(f => ({ id: f, label: f }));
          setAvailableFields(fields);
          const defaults = SMART_DEFAULTS[queryObj] || hardcoded;
          setSelectedFields(defaults.filter(d => hardcoded.includes(d)));
          setStatus('Using built-in fields (inspect not supported for this object)');
          return;
        }

        if (!isTestMode) {
          const liveFields = await inspectIntacctObject(config, queryObj);
          const sortedFields = [...(Array.isArray(liveFields) ? liveFields : [])].sort((a, b) => a.id.localeCompare(b.id));
          setAvailableFields(sortedFields);
          const defaults = SMART_DEFAULTS[queryObj] || [];
          let finalSelection = defaults.filter(defId => sortedFields.some(f => f.id === defId));
          if (finalSelection.length === 0) {
            if (sortedFields.some(f => f.id === 'RECORDNO')) finalSelection = ['RECORDNO'];
            else if (sortedFields.length > 0) finalSelection = [sortedFields[0].id];
          }
          if (selectedFields.length === 0) setSelectedFields(finalSelection);
        } else {
          setAvailableFields([{ id: 'RECORDNO', label: 'Record No' }, { id: 'TEST', label: 'Test Field' }]);
        }
      } catch (err) {
        console.error("Inspect failed", err); setStatus(`Inspect Error: ${err.message}`);
        setAvailableFields((SMART_DEFAULTS[queryObj] || ['RECORDNO']).map(d => ({ id: d, label: d })));
      } finally { setIsInspecting(false); }
    };
    fetchSchema();
  }, [queryObj, config.companyId, mode, config.useSessionId, config.sessionId, config.userId, config.userPassword]);

  useEffect(() => {
    if (mode !== 'purchasing') return;
    const fetchPoSchema = async () => {
      setIsInspecting(true); setStatus('');
      try {
        const liveFields = await inspectIntacctObject(config, PO_CONVERSION_CONFIG.sourceObject);
        setPoAvailableFields([...liveFields].sort((a, b) => a.id.localeCompare(b.id)));
      } catch (err) {
        setStatus(`Inspect Error: ${err.message}`);
        setPoAvailableFields(PO_CONVERSION_CONFIG.queryFields.map(f => ({ id: f, label: f })));
      } finally { setIsInspecting(false); }
    };
    fetchPoSchema();
  }, [mode, config.companyId, config.useSessionId, config.sessionId, config.userId, config.userPassword]);

  const filteredObjects = useMemo(() => {
    let list = category === 'All' ? OBJECT_LIBRARY : OBJECT_LIBRARY.filter(o => o.category === category);
    if (mode === 'update' || mode === 'create') list = list.filter(o => WRITE_SUPPORTED_OBJECTS.has(o.id));
    return list;
  }, [category, mode]);

  // ============ UTILITY FUNCTIONS ============
  const resolveDynamicFilters = async (rawQuery) => {
    const refRegex = /REF\((.*?)\)/g; let match; let resolvedQuery = rawQuery; const replacements = [];
    while ((match = refRegex.exec(rawQuery)) !== null) replacements.push({ fullMatch: match[0], address: match[1] });
    for (const rep of replacements) {
      try {
        let valuesString = '';
        await Excel.run(async (context) => {
          const sheetName = rep.address.split('!')[0].replace(/'/g, ''); const rangeAddr = rep.address.split('!')[1];
          const sheet = context.workbook.worksheets.getItem(sheetName);
          const range = sheet.getRange(rangeAddr).getIntersection(sheet.getUsedRange());
          range.load(["values", "rowIndex"]); await context.sync();
          let rawValues = range.values; if (range.rowIndex === 0 && rawValues.length > 1) rawValues.shift();
          valuesString = rawValues.flat().filter(v => v !== "" && v != null).map(v => `'${v}'`).join(',');
        });
        resolvedQuery = resolvedQuery.replace(rep.fullMatch, valuesString ? `(${valuesString})` : `('0')`);
      } catch (err) { console.error("Failed to resolve dynamic range:", rep.address, err); }
    }
    return resolvedQuery;
  };

  const loadSettingsFromActiveSheet = async () => {
    if (isFilterBuilderOpenRef.current) return;
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const settingsKey = sheet.customProperties.getItemOrNullObject("IntacctConfig"); await context.sync();
        if (!settingsKey.isNullObject) {
          settingsKey.load("value"); await context.sync();
          const savedConfig = JSON.parse(settingsKey.value);
          if (savedConfig.category) setCategory(savedConfig.category);
          if (savedConfig.object) setQueryObj(savedConfig.object);
          const fields = Array.isArray(savedConfig.fields) ? savedConfig.fields : (savedConfig.fields ? savedConfig.fields.split(',') : []);
          if (fields.length > 0) setSelectedFields(fields);
          if (savedConfig.query) setSqlFilter(savedConfig.query);
          if (savedConfig.useLabelHeaders !== undefined) setUseLabelHeaders(savedConfig.useLabelHeaders);
          setShowPrivate(savedConfig.showPrivate || false); setOrderBy(savedConfig.orderBy || null);
          // Load legacy query settings if present
          if (savedConfig.useLegacyQuery !== undefined) setUseLegacyQuery(savedConfig.useLegacyQuery);
          if (savedConfig.legacyQueryString !== undefined) setLegacyQueryString(savedConfig.legacyQueryString);
        }
      });
    } catch (error) { console.error(error); } finally { setLoading(false); }
  };

  // ============ EXCEL WRITE OPERATIONS ============
  const writeToExcel = async (data, keys) => {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      // Save config including legacy query settings
      sheet.customProperties.add("IntacctConfig", JSON.stringify({ 
        object: queryObj, 
        fields: selectedFields, 
        query: sqlFilter, 
        category, 
        useLabelHeaders, 
        pageSize, 
        showPrivate, 
        orderBy,
        useLegacyQuery,
        legacyQueryString
      }));
      if (data.length > 0) {
        const headers = useLabelHeaders ? keys.map(k => availableFields.find(f => f.id === k)?.label || k) : keys;
        sheet.getRange().clear();
        const headerRange = sheet.getRange("A1").getResizedRange(0, keys.length - 1);
        headerRange.values = [headers]; headerRange.format.font.bold = true; headerRange.format.fill.color = "#F3F4F6";
        const dataRange = sheet.getRange("A2").getResizedRange(data.length - 1, keys.length - 1);
        dataRange.numberFormat = Array(data.length).fill(Array(keys.length).fill("@"));
        dataRange.values = data.map(row => keys.map(k => row[k] || ""));
        sheet.getUsedRange().format.autofitColumns();
      }
      await context.sync();
    });
  };

  // Write GET results with nested data to multiple sheets
  const writeGetResultsToExcel = async (data, objectName) => {
    if (!data || data.length === 0) return { mainFields: 0, nestedSheets: [] };
    
    // Identify nested arrays vs flat fields
    const nestedArrayFields = {}; // { fieldName: { maxIndex: N, data: [[row1entries], [row2entries], ...] } }
    const flatRecords = [];
    
    data.forEach((record, recordIndex) => {
      const flatRecord = {};
      
      Object.entries(record).forEach(([key, value]) => {
        if (value && typeof value === 'object') {
          // Check if it's an array or has nested array structure
          let entries = [];
          
          if (Array.isArray(value)) {
            entries = value;
          } else if (typeof value === 'object') {
            // Check for nested array patterns like WAREHOUSEINFO.itemwarehouseinfo
            const nestedKey = Object.keys(value).find(k => Array.isArray(value[k]));
            if (nestedKey) {
              entries = value[nestedKey];
            } else {
              // Single nested object - try to find the child array
              const childKey = Object.keys(value).find(k => 
                value[k] && typeof value[k] === 'object' && !Array.isArray(value[k])
              );
              if (childKey && value[childKey]) {
                // Could be a single entry, wrap it
                entries = [value[childKey]];
              } else {
                // Flatten simple nested objects into the main record
                Object.entries(value).forEach(([nestedK, nestedV]) => {
                  if (typeof nestedV !== 'object' || nestedV === null) {
                    flatRecord[`${key}.${nestedK}`] = nestedV;
                  }
                });
                return;
              }
            }
          }
          
          if (entries.length > 0) {
            if (!nestedArrayFields[key]) {
              nestedArrayFields[key] = { maxIndex: 0, data: [] };
            }
            nestedArrayFields[key].data[recordIndex] = entries;
            nestedArrayFields[key].maxIndex = Math.max(nestedArrayFields[key].maxIndex, entries.length);
          }
        } else {
          flatRecord[key] = value;
        }
      });
      
      flatRecords.push(flatRecord);
    });
    
    // Get all unique flat field names
    const flatFields = [...new Set(flatRecords.flatMap(r => Object.keys(r)))];
    
    await Excel.run(async (context) => {
      const workbook = context.workbook;
      const sheets = workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      
      const existingNames = sheets.items.map(s => s.name);
      
      // Write main sheet with flat data
      const mainSheet = workbook.worksheets.getActiveWorksheet();
      mainSheet.load("name");
      await context.sync();
      
      mainSheet.getRange().clear();
      if (flatRecords.length > 0 && flatFields.length > 0) {
        const headerRange = mainSheet.getRange("A1").getResizedRange(0, flatFields.length - 1);
        headerRange.values = [flatFields];
        headerRange.format.font.bold = true;
        headerRange.format.fill.color = "#DBEAFE"; // Light blue for GET results
        
        const dataRange = mainSheet.getRange("A2").getResizedRange(flatRecords.length - 1, flatFields.length - 1);
        dataRange.numberFormat = Array(flatRecords.length).fill(Array(flatFields.length).fill("@"));
        dataRange.values = flatRecords.map(row => flatFields.map(k => {
          const val = row[k];
          if (val === null || val === undefined) return "";
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val);
        }));
        
        mainSheet.getUsedRange().format.autofitColumns();
      }
      
      // Write nested arrays to separate sheets by index
      const nestedSheetInfo = [];
      for (const [fieldName, fieldData] of Object.entries(nestedArrayFields)) {
        for (let idx = 0; idx < fieldData.maxIndex; idx++) {
          const sheetName = `${fieldName.replace(/INFO$/i, '')} ${idx + 1}`.substring(0, 31); // Excel sheet name limit
          
          // Collect all entries at this index position across all records
          const entriesAtIndex = [];
          const parentKeys = []; // Track which parent record each entry belongs to
          
          fieldData.data.forEach((recordEntries, recordIndex) => {
            if (recordEntries && recordEntries[idx]) {
              const entry = recordEntries[idx];
              // Add parent identifier (like ITEMID) if available
              const parentRecord = flatRecords[recordIndex];
              const parentId = parentRecord.ITEMID || parentRecord.VENDORID || parentRecord.CUSTOMERID || parentRecord.RECORDNO || recordIndex;
              entriesAtIndex.push({ ...entry, _PARENT_ID: parentId });
            }
          });
          
          if (entriesAtIndex.length === 0) continue;
          
          // Get all fields from these entries
          const entryFields = ['_PARENT_ID', ...new Set(entriesAtIndex.flatMap(e => Object.keys(e).filter(k => k !== '_PARENT_ID')))];
          
          // Create or get sheet
          let sheet;
          if (existingNames.includes(sheetName)) {
            sheet = sheets.getItem(sheetName);
            sheet.getRange().clear();
          } else {
            sheet = sheets.add(sheetName);
          }
          
          // Write headers
          const headerRange = sheet.getRange("A1").getResizedRange(0, entryFields.length - 1);
          headerRange.values = [entryFields.map(f => f === '_PARENT_ID' ? `Parent ${objectName}` : f)];
          headerRange.format.font.bold = true;
          headerRange.format.fill.color = "#FEF3C7"; // Yellow for nested sheets
          
          // Write data
          const dataRange = sheet.getRange("A2").getResizedRange(entriesAtIndex.length - 1, entryFields.length - 1);
          dataRange.numberFormat = Array(entriesAtIndex.length).fill(Array(entryFields.length).fill("@"));
          dataRange.values = entriesAtIndex.map(entry => entryFields.map(k => {
            const val = entry[k];
            if (val === null || val === undefined) return "";
            if (typeof val === 'object') return JSON.stringify(val);
            return String(val);
          }));
          
          sheet.getUsedRange().format.autofitColumns();
          nestedSheetInfo.push({ name: sheetName, count: entriesAtIndex.length });
        }
      }
      
      // Activate main sheet
      mainSheet.activate();
      await context.sync();
      
      return { mainFields: flatFields.length, nestedSheets: nestedSheetInfo };
    });
    
    return { mainFields: flatFields.length, nestedSheets: Object.keys(nestedArrayFields) };
  };

  const writePOConversionToExcel = async (transformedData) => {
    const headers = PO_CONVERSION_CONFIG.outputColumns.map(col => col.name);
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.customProperties.add("IntacctConfig", JSON.stringify({ type: 'po-conversion', query: poFilter, showPrivate: poShowPrivate, orderBy: poOrderBy }));
      if (transformedData.length > 0) {
        sheet.getRange().clear();
        const headerRange = sheet.getRange("A1").getResizedRange(0, headers.length - 1);
        headerRange.values = [headers]; headerRange.format.font.bold = true; headerRange.format.fill.color = "#FEF3C7";
        const dataRange = sheet.getRange("A2").getResizedRange(transformedData.length - 1, headers.length - 1);
        dataRange.numberFormat = Array(transformedData.length).fill(Array(headers.length).fill("@"));
        dataRange.values = transformedData.map(row => headers.map(h => row[h] !== null && row[h] !== undefined ? String(row[h]) : ""));
        sheet.getUsedRange().format.autofitColumns();
      }
      await context.sync();
    });
  };

  // ============ HANDLERS ============
  const handleObjectChange = (newObj) => { setQueryObj(newObj); setSelectedFields(SMART_DEFAULTS[newObj] || ['RECORDNO']); setSqlFilter("RECORDNO > 0"); setOrderBy(null); setLegacyQueryString(''); };
  
  const handleOpenFilterBuilder = async () => {
    isFilterBuilderOpenRef.current = true;
    try { await Excel.run(async (context) => { const sheet = context.workbook.worksheets.getActiveWorksheet(); sheet.load("name"); await context.sync(); filterOriginSheet.current = sheet.name; }); } catch (e) { console.error(e); }
    setIsFilterBuilderOpen(true);
  };
  
  const handleCloseFilterBuilder = () => { isFilterBuilderOpenRef.current = false; setIsFilterBuilderOpen(false); };
  
  const handleFilterApply = async (query, newShowPrivate, newOrderBy) => {
    setSqlFilter(query); setShowPrivate(newShowPrivate || false); setOrderBy(newOrderBy || null);
    try { await Excel.run(async (context) => { const sheet = context.workbook.worksheets.getActiveWorksheet(); sheet.customProperties.add("IntacctConfig", JSON.stringify({ object: queryObj, fields: selectedFields, query, category, useLabelHeaders, pageSize, showPrivate: newShowPrivate || false, orderBy: newOrderBy || null, useLegacyQuery, legacyQueryString })); await context.sync(); }); } catch (error) { console.error("Error saving filter:", error); }
  };

  const handleOpenPoFilterBuilder = async () => {
    isFilterBuilderOpenRef.current = true;
    try { await Excel.run(async (context) => { const sheet = context.workbook.worksheets.getActiveWorksheet(); sheet.load("name"); await context.sync(); filterOriginSheet.current = sheet.name; }); } catch (e) { console.error(e); }
    setIsPoFilterBuilderOpen(true);
  };
  
  const handleClosePoFilterBuilder = () => { isFilterBuilderOpenRef.current = false; setIsPoFilterBuilderOpen(false); };
  const handlePoFilterApply = (query, newShowPrivate, newOrderBy) => { setPoFilter(query); setPoShowPrivate(newShowPrivate || false); setPoOrderBy(newOrderBy || null); };

  const generatePreviewXml = async () => {
    // GET Mode preview
    if (useGetMode) {
      try {
        const keys = await resolveGetKeys();
        if (keys.length === 0) {
          setStatus('Error: No keys found in selected range.');
          return;
        }
        const xml = buildGetPreviewXml(queryObj, keys, getKeyType, config);
        setXmlContent(xml); 
        setIsXmlPreviewOpen(true);
      } catch (err) {
        setStatus(`Error: ${err.message}`);
      }
      return;
    }
    
    if (useLegacyQuery) {
      // Generate legacy readByQuery XML preview
      let authXml = config.useSessionId && config.sessionId ? `<sessionid>${config.sessionId}</sessionid>` : `<login>\n        <userid>${config.userId}</userid>\n        <companyid>${config.companyId}</companyid>\n        <password>${config.userPassword}</password>${config.locationId ? `\n        <locationid>${config.locationId}</locationid>` : ''}\n      </login>`;
      const fieldsString = selectedFields.join(',');
      const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<request>\n  <control>\n    <senderid>${config.senderId}</senderid>\n    <password>********</password>\n    <controlid>${new Date().getTime()}</controlid>\n    <uniqueid>false</uniqueid>\n    <dtdversion>3.0</dtdversion>\n  </control>\n  <operation>\n    <authentication>\n      ${authXml}\n    </authentication>\n    <content>\n      <function controlid="readByQuery-func">\n        <readByQuery>\n          <object>${queryObj}</object>\n          <fields>${fieldsString}</fields>\n          <query>${escapeXml(legacyQueryString)}</query>\n          <pagesize>1000</pagesize>\n        </readByQuery>\n      </function>\n    </content>\n  </operation>\n</request>`;
      setXmlContent(xml); setIsXmlPreviewOpen(true);
      return;
    }
    
    const resolvedFilter = await resolveDynamicFilters(sqlFilter);
    let authXml = config.useSessionId && config.sessionId ? `<sessionid>${config.sessionId}</sessionid>` : `<login>\n        <userid>${config.userId}</userid>\n        <companyid>${config.companyId}</companyid>\n        <password>${config.userPassword}</password>${config.locationId ? `\n        <locationid>${config.locationId}</locationid>` : ''}\n      </login>`;
    const filterXml = buildFilterPreview(resolvedFilter);
    const fieldsXml = selectedFields.map(f => `<field>${f}</field>`).join('\n            ');
    let orderByXml = '';
    if (orderBy && Array.isArray(orderBy) && orderBy.length > 0) {
      const ordersXml = orderBy.filter(o => o.field).map(o => `<order>\n            <field>${o.field}</field>\n            ${o.descending ? '<descending />' : '<ascending />'}\n          </order>`).join('\n          ');
      if (ordersXml) orderByXml = `\n        <orderby>\n          ${ordersXml}\n        </orderby>`;
    }
    const optionsXml = showPrivate ? `\n        <options>\n          <showprivate>true</showprivate>\n        </options>` : '';
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<request>\n  <control>\n    <senderid>${config.senderId}</senderid>\n    <password>********</password>\n    <controlid>${new Date().getTime()}</controlid>\n    <uniqueid>false</uniqueid>\n    <dtdversion>3.0</dtdversion>\n  </control>\n  <operation>\n    <authentication>\n      ${authXml}\n    </authentication>\n    <content>\n      <function controlid="query-func">\n        <query>\n          <object>${queryObj}</object>\n          <select>\n            ${fieldsXml}\n          </select>\n          ${filterXml}${orderByXml}${optionsXml}\n          <pagesize>${pageSize}</pagesize>\n          <offset>0</offset>\n        </query>\n      </function>\n    </content>\n  </operation>\n</request>`;
    setXmlContent(xml); setIsXmlPreviewOpen(true);
  };

  // ============ REPORT HANDLERS ============
  const handleRunReport = async () => {
    setLoading(true); setStatus('Processing...'); setPendingDownload(null);
    try {
      // Test mode
      if (isTestMode) { 
        await new Promise(r => setTimeout(r, 800)); 
        await writeToExcel([{ RECORDNO: "1", TITLE: "Cash", ACCOUNTNO: "1000" }], selectedFields); 
        setStatus('Success! (Test Mode)'); 
        setLoading(false);
        return; 
      }
      
      // GET Mode (read/readByName) - fetches complete records by key
      if (useGetMode) {
        setStatus('Reading keys from Excel...');
        const keys = await resolveGetKeys();
        if (keys.length === 0) {
          throw new Error('No keys found in selected range.');
        }
        
        setStatus(`Fetching ${keys.length} record(s) by ${getKeyType === 'recordno' ? 'RECORDNO' : 'Name'}...`);
        const response = await fetchIntacctDataByGet(config, queryObj, keys, getKeyType);
        const { data, metadata, rawXml } = response;
        
        // Download response XML if option is enabled
        if (exportGetXml && rawXml) {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          
          // Download response XML if backend provided it
          if (rawXml.responses && rawXml.responses.length > 0) {
            const responseXml = rawXml.responses.length === 1 
              ? rawXml.responses[0] 
              : rawXml.responses.map((xml, i) => `<!-- Batch ${i + 1} Response -->\n${xml}`).join('\n\n');
            downloadXmlFile(responseXml, `${queryObj}_Response_${timestamp}.xml`, true);
          } 
          // Fallback: download request XML if no response XML available
          else if (rawXml.requests && rawXml.requests.length > 0) {
            const requestXml = rawXml.requests.length === 1 
              ? rawXml.requests[0] 
              : rawXml.requests.map((xml, i) => `<!-- Batch ${i + 1} Request -->\n${xml}`).join('\n\n');
            downloadXmlFile(requestXml, `${queryObj}_Request_${timestamp}.xml`, true);
            console.log('Note: Downloading request XML. Update server.js to enable response XML export.');
          }
        }
        
        if (data.length === 0) {
          throw new Error('No records returned. Check that the keys are valid.');
        }
        
        setStatus(`Writing ${data.length} records to Excel (checking for nested data)...`);
        const writeResult = await writeGetResultsToExcel(data, queryObj);
        
        const nestedInfo = writeResult.nestedSheets && writeResult.nestedSheets.length > 0 
          ? ` + ${writeResult.nestedSheets.length} nested sheet(s)` 
          : '';
        const errorsNote = metadata.errors ? ` (${metadata.errors.length} errors)` : '';
        const xmlNote = exportGetXml ? ' (XML exported)' : '';
        setStatus(`Success! Retrieved ${data.length} of ${metadata.totalRequested} records${nestedInfo}${errorsNote}${xmlNote}`);
        setLoading(false);
        return;
      }
      
      // Legacy Query Mode (readByQuery) - auto-paginates all results
      if (useLegacyQuery) {
        setStatus('Querying Intacct (Legacy Mode)...');
        const fieldsString = selectedFields.join(',');
        const response = await fetchIntacctDataLegacy(config, queryObj, fieldsString, legacyQueryString, 1000, true);
        const { data, metadata } = response;
        
        setStatus(`Writing ${data.length} records...`);
        await writeToExcel(data, selectedFields);
        
        const pagesInfo = metadata.complete ? '' : ' (may be incomplete)';
        setStatus(`Success! Found ${data.length} records in ${metadata.pagesFetched} page(s)${pagesInfo}`);
        setLoading(false);
        return;
      }
      
      // Standard Query Mode
      const resolvedFilter = await resolveDynamicFilters(sqlFilter);
      setStatus('Querying Intacct...');
      const response = await fetchIntacctData(config, queryObj, selectedFields.join(','), resolvedFilter, pageSize, 0, showPrivate, orderBy);
      const { data: firstPageData, metadata } = response;
      const totalCount = metadata?.totalcount || firstPageData.length;
      
      // Large dataset confirmation
      if (totalCount > 5000) { 
        setPendingDownload({ initialData: firstPageData, totalCount, currentOffset: firstPageData.length, fieldsString: selectedFields.join(','), resolvedFilter, showPrivate, orderBy }); 
        setLoading(false); 
        return; 
      }
      
      // Fetch all pages
      let allData = [...firstPageData]; 
      let currentOffset = firstPageData.length;
      while (currentOffset < totalCount) { 
        setStatus(`Fetching ${allData.length} / ${totalCount}...`); 
        const moreRes = await fetchMoreIntacctData(config, queryObj, selectedFields.join(','), resolvedFilter, pageSize, currentOffset, showPrivate, orderBy); 
        if (moreRes.data?.length > 0) { 
          allData = [...allData, ...moreRes.data]; 
          currentOffset += moreRes.data.length; 
        } else break; 
      }
      
      setStatus(`Writing ${allData.length} records...`); 
      await writeToExcel(allData, selectedFields); 
      setStatus(`Success! Found ${allData.length} records.`);
    } catch (error) { 
      setStatus(`Error: ${error.message}`); 
    } finally { 
      if (!pendingDownload) setLoading(false); 
    }
  };

  const confirmDownload = async () => {
    if (!pendingDownload) return; setLoading(true);
    const { initialData, totalCount, currentOffset, fieldsString, resolvedFilter, showPrivate: dlShowPrivate, orderBy: dlOrderBy } = pendingDownload;
    let allData = [...initialData]; let offset = currentOffset;
    try {
      while (offset < totalCount) { setStatus(`Fetching ${allData.length} / ${totalCount}...`); const moreRes = await fetchMoreIntacctData(config, queryObj, fieldsString, resolvedFilter, pageSize, offset, dlShowPrivate, dlOrderBy); if (moreRes.data?.length > 0) { allData = [...allData, ...moreRes.data]; offset += moreRes.data.length; } else break; }
      await writeToExcel(allData, selectedFields); setStatus(`Success! Found ${allData.length} records.`); setPendingDownload(null);
    } catch (error) { setStatus(`Error: ${error.message}`); } finally { setLoading(false); }
  };
  const cancelDownload = () => { setPendingDownload(null); setStatus('Download cancelled.'); setLoading(false); };

  // ============ PO HANDLERS ============
  const handleLoadPOLines = async () => {
    setLoading(true); setStatus('Loading PO lines...');
    try {
      const resolvedFilter = await resolveDynamicFilters(poFilter);
      const fieldsString = PO_CONVERSION_CONFIG.queryFields.join(',');
      const response = await fetchIntacctData(config, PO_CONVERSION_CONFIG.sourceObject, fieldsString, resolvedFilter, pageSize, 0, poShowPrivate, poOrderBy);
      let allData = [...response.data]; let currentOffset = allData.length;
      const totalCount = response.metadata?.totalcount || allData.length;
      while (currentOffset < totalCount) {
        setStatus(`Fetching ${allData.length} / ${totalCount}...`);
        const moreRes = await fetchMoreIntacctData(config, PO_CONVERSION_CONFIG.sourceObject, fieldsString, resolvedFilter, pageSize, currentOffset, poShowPrivate, poOrderBy);
        if (moreRes.data?.length > 0) { allData = [...allData, ...moreRes.data]; currentOffset += moreRes.data.length; } else break;
      }
      const transformedData = transformPOData(allData, PO_CONVERSION_CONFIG);
      await writePOConversionToExcel(transformedData);
      setStatus(`Success! Loaded ${transformedData.length} PO lines ready for conversion.`);
    } catch (error) { setStatus(`Error: ${error.message}`); } finally { setLoading(false); }
  };

  const handlePreviewPOCreate = async () => {
    setLoading(true); setStatus("Reading selection for PO create...");
    try {
      let records = [];
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange().getIntersectionOrNullObject(context.workbook.worksheets.getActiveWorksheet().getUsedRange());
        range.load("values, rowCount, isNullObject"); await context.sync();
        if (range.isNullObject || !range.values || range.values.length < 2) throw new Error("Select headers AND data rows.");
        const headers = range.values[0].map(h => String(h).toLowerCase().trim());
        if (!headers.includes('documentno')) throw new Error("Selection MUST include 'documentno' column.");
        if (!headers.includes('transactiontype')) throw new Error("Selection MUST include 'transactiontype' column.");
        for (let i = 1; i < range.values.length; i++) {
          const row = range.values[i]; if (row.every(cell => cell === "")) continue;
          if (!row[headers.indexOf('documentno')] || row[headers.indexOf('documentno')] === "") continue;
          const record = {}; headers.forEach((h, idx) => { if (row[idx] !== "" && row[idx] !== null && row[idx] !== undefined) record[h] = row[idx]; }); records.push(record);
        }
      });
      if (records.length === 0) throw new Error("No valid records found with documentno.");
      const summary = getPOCreateSummary(records);
      if (summary.missingDocNoRows.length > 0) throw new Error(`Rows ${summary.missingDocNoRows.join(', ')} are missing documentno.`);
      setPoCreateData({ records, summary, previewXml: buildPOCreatePreviewXml(records, poExcludedFields) });
      setPoCreateModalOpen(true);
    } catch (err) { setStatus(`Error: ${err.message}`); } finally { setLoading(false); }
  };

  const confirmPOCreate = async () => {
    if (!poCreateData) return;
    setPoCreateModalOpen(false); setLoading(true);
    const { records, summary } = poCreateData;
    setStatus(`Creating ${summary.totalDocuments} PO transaction(s)...`);
    try {
      if (isTestMode) { await new Promise(r => setTimeout(r, 1000)); setStatus("Success! (Test Mode)"); }
      else {
        const results = await createPOTransactions(config, records, poExcludedFields);
        await writeGroupedResultsToExcel(results, records, 'documentno');
        const successCount = results?.filter(r => r.status === 'success').length || 0;
        setStatus(successCount === summary.totalDocuments ? `Success! Created ${successCount} PO transaction(s).` : `Completed. Success: ${successCount}/${summary.totalDocuments}`);
      }
    } catch (err) { setStatus(`Failed: ${err.message}`); } finally { setLoading(false); setPoCreateData(null); }
  };

  const handleExcludedFieldsChange = (fieldId) => {
    const newExcluded = poExcludedFields.includes(fieldId) ? poExcludedFields.filter(f => f !== fieldId) : [...poExcludedFields, fieldId];
    setPoExcludedFields(newExcluded);
    if (poCreateData) setPoCreateData({ ...poCreateData, previewXml: buildPOCreatePreviewXml(poCreateData.records, newExcluded) });
  };

  // ============ WRITE HANDLERS ============
  const writeVendorBankHeaders = async () => {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const headers = getVendorBankSuggestedHeaders();
        const headerRange = sheet.getRangeByIndexes(0, 0, 1, headers.length);
        headerRange.values = [headers];
        headerRange.format.font.bold = true;
        headerRange.format.autofitColumns();
        await context.sync();
      });
      setStatus("Headers added! Fill in your data, then click Preview Create again.");
    } catch (err) { setStatus(`Error writing headers: ${err.message}`); }
    setVendorBankHeadersModalOpen(false);
  };

  const handleWriteClick = async () => {
    setLoading(true); setStatus("Reading selection...");
    try {
      let records = [], detectedHeaders = [], detectedContractLineParentKey = null, detectedHeadersRaw = [];

      // For VENDORBANKFILEDETAIL in create mode, check if the sheet is blank first
      const isVendorBank = isVendorBankDetailType(queryObj);
      if (isVendorBank && mode === 'create') {
        let sheetIsBlank = false;
        await Excel.run(async (context) => {
          const sheet = context.workbook.worksheets.getActiveWorksheet();
          const usedRange = sheet.getUsedRangeOrNullObject(true);
          usedRange.load("isNullObject, values, rowCount, columnCount");
          await context.sync();
          if (usedRange.isNullObject) {
            sheetIsBlank = true;
          } else if (usedRange.rowCount === 1 && usedRange.values[0].every(cell => cell === "")) {
            sheetIsBlank = true;
          }
        });
        if (sheetIsBlank) {
          setLoading(false);
          setVendorBankHeadersModalOpen(true);
          return;
        }
      }
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange().getIntersectionOrNullObject(context.workbook.worksheets.getActiveWorksheet().getUsedRange());
        range.load("values, rowCount, isNullObject"); await context.sync();
        if (range.isNullObject || !range.values || range.values.length < 2) throw new Error("Select headers AND data rows.");
        // Respect Excel filtering: load per-row visibility so filtered-out / hidden rows are skipped.
        // Catches BOTH AutoFilter-hidden rows and manually hidden rows. When nothing is filtered, no rows are hidden, so all rows go through as before.
        const rowVisibility = [];
        for (let i = 0; i < range.values.length; i++) { const r = range.getRow(i); r.load("rowHidden"); rowVisibility.push(r); }
        await context.sync();
        const headers = range.values[0].map(h => String(h).toUpperCase().trim());
        detectedHeaders = headers;
        detectedHeadersRaw = headers; // preserve for document entry header/line splitting
        const isRateTable = isRateTableEntryType(queryObj), isDocEntry = isDocumentEntryType(queryObj), isItemNested = isItemNestedEntryType(queryObj), isContractLineEntry = isContractLineEntryType(queryObj);
        const docConfig = isDocEntry ? getDocumentEntryConfig(queryObj) : null;
        const docKeyHeader = isDocEntry ? resolveDocumentKeyHeader(queryObj, headers) : null;
        const itemNestedConfig = isItemNested ? getItemNestedConfig(queryObj) : null;
        const contractLineParentKey = isContractLineEntry ? detectContractLineParentKey(headers) : null;
        detectedContractLineParentKey = contractLineParentKey;
        if (mode === 'update') {
          if (isRateTable && !headers.includes('RATETABLEID')) throw new Error("Rate table entries MUST include 'RATETABLEID' column.");
          else if (isContractLineEntry && !contractLineParentKey) throw new Error("PROJECTCONTRACTLINEENTRY updates MUST include either 'PROJECTCONTRACTLINEID' or 'PROJECTCONTRACTLINE.RECORDNO' column.");
          else if (isItemNested && !headers.includes(itemNestedConfig.parentKeyField)) throw new Error(`${queryObj} entries MUST include '${itemNestedConfig.parentKeyField}' column.`);
          else if (isDocEntry && !docKeyHeader) throw new Error(`Document entries MUST include a ${(docConfig.keyAliases || [docConfig.parentKeyField]).join(' or ')} column.`);
          else if (!isRateTable && !isDocEntry && !isItemNested && !isContractLineEntry && !headers.includes('RECORDNO')) throw new Error("Selection MUST include 'RECORDNO' column.");
          if (isDocEntry && docConfig.lineNoField && !headers.includes(docConfig.lineNoField)) throw new Error(`Document entries MUST include '${docConfig.lineNoField}' column.`);
        }
        if (mode === 'create' && isVendorBank && !headers.includes('VENDORID')) throw new Error("VENDORBANKFILEDETAIL requires a 'VENDORID' column.");
        for (let i = 1; i < range.values.length; i++) {
          const row = range.values[i]; if (row.every(cell => cell === "")) continue;
          if (rowVisibility[i] && rowVisibility[i].rowHidden) continue; // skip filtered-out / hidden rows
          if (mode === 'update') {
            if (isRateTable && !row[headers.indexOf('RATETABLEID')]) continue;
            else if (isContractLineEntry && !row[headers.indexOf(contractLineParentKey)]) continue;
            else if (isItemNested && !row[headers.indexOf(itemNestedConfig.parentKeyField)]) continue;
            else if (isDocEntry && !row[headers.indexOf(docKeyHeader)]) continue;
            else if (!isRateTable && !isDocEntry && !isItemNested && !isContractLineEntry && !row[headers.indexOf('RECORDNO')]) continue;
          }
          if (mode === 'create' && isVendorBank && !row[headers.indexOf('VENDORID')]) continue;
          const record = {}; headers.forEach((h, idx) => { if (row[idx] !== "") record[h] = row[idx]; }); records.push(record);
        }
      });
      if (records.length === 0) throw new Error("No valid records found.");
      
      if (mode === 'create' && isJobCostSetupFlow(detectedHeaders)) {
        setJobCostData({ records, summary: getJobCostSetupSummary(records), previewXml: buildJobCostPreviewXml(records) });
        setJobCostResults(null); setJobCostModalOpen(true); setLoading(false); return;
      }
      
      const isRateTable = isRateTableEntryType(queryObj), isDocEntry = isDocumentEntryType(queryObj), isTransRule = isTransactionRuleType(queryObj), isItemNested = isItemNestedEntryType(queryObj), isContractLineEntry = isContractLineEntryType(queryObj), isVendorBankCreate = isVendorBankDetailType(queryObj) && mode === 'create';
      if (isRateTable && mode === 'update') {
        const summary = getRateTableUpdateSummary(queryObj, records);
        if (summary.missingIdRows.length > 0) throw new Error(`Rows ${summary.missingIdRows.join(', ')} are missing RATETABLEID.`);
        setPendingUpdateData({ records, xmlSnippet: buildRateTablePreviewXml(queryObj, records), isRateTable: true, isDocEntry: false, isTransRule: false, isItemNested: false, isContractLineEntry: false, isVendorBank: false, rateTableSummary: summary });
      } else if (isRateTable && mode === 'create') {
        const summary = getRateTableCreateSummary(queryObj, records);
        if (summary.missingIdRows.length > 0) throw new Error(`Rows ${summary.missingIdRows.join(', ')} are missing RATETABLEID.`);
        if (summary.missingNameTables.length > 0) throw new Error(`Rate table(s) ${summary.missingNameTables.join(', ')} are missing required NAME field.`);
        setPendingUpdateData({ records, xmlSnippet: buildRateTableCreatePreviewXml(queryObj, records), isRateTable: true, isDocEntry: false, isTransRule: false, isItemNested: false, isContractLineEntry: false, isVendorBank: false, rateTableSummary: summary });
      } else if (isItemNested && mode === 'update') {
        const summary = getItemNestedUpdateSummary(queryObj, records);
        if (summary.missingIdRows.length > 0) throw new Error(`Rows ${summary.missingIdRows.join(', ')} are missing ${summary.config.parentKeyField}.`);
        setPendingUpdateData({ records, xmlSnippet: buildItemNestedPreviewXml(queryObj, records), isRateTable: false, isDocEntry: false, isTransRule: false, isItemNested: true, isContractLineEntry: false, isVendorBank: false, itemNestedSummary: summary });
      } else if (isContractLineEntry && mode === 'update') {
        const parentKey = detectedContractLineParentKey;
        const summary = getContractLineUpdateSummary(queryObj, records, parentKey);
        if (summary.missingParentKeyRows.length > 0) throw new Error(`Rows ${summary.missingParentKeyRows.join(', ')} are missing ${parentKey}.`);
        setPendingUpdateData({ records, xmlSnippet: buildContractLinePreviewXml(queryObj, records, parentKey), isRateTable: false, isDocEntry: false, isTransRule: false, isItemNested: false, isContractLineEntry: true, isVendorBank: false, contractLineSummary: summary });
      } else if (isTransRule && mode === 'create') {
        const summary = getTransactionRuleCreateSummary(records);
        if (summary.missingNameRows.length > 0) throw new Error(`Rows ${summary.missingNameRows.join(', ')} are missing NAME.`);
        if (summary.missingJournalIdRules.length > 0) throw new Error(`Transaction rule(s) "${summary.missingJournalIdRules.join(', ')}" are missing required JOURNALID field.`);
        setPendingUpdateData({ records, xmlSnippet: buildTransactionRulePreviewXml(records), isRateTable: false, isDocEntry: false, isTransRule: true, isItemNested: false, isContractLineEntry: false, isVendorBank: false, transRuleSummary: summary });
      } else if (isVendorBankCreate) {
        const summary = getVendorBankCreateSummary(records);
        if (summary.missingIdRows.length > 0) throw new Error(`Rows ${summary.missingIdRows.join(', ')} are missing VENDORID.`);
        setPendingUpdateData({ records, xmlSnippet: buildVendorBankPreviewXml(records), isRateTable: false, isDocEntry: false, isTransRule: false, isItemNested: false, isContractLineEntry: false, isVendorBank: true, vendorBankSummary: summary });
      } else if (isDocEntry && mode === 'update') {
        const summary = getDocumentUpdateSummary(queryObj, records, detectedHeadersRaw);
        if (summary.missingDocIdRows.length > 0) throw new Error(`Rows ${summary.missingDocIdRows.join(', ')} are missing ${summary.keyHeader || summary.config.parentKeyField}.`);
        if (summary.missingLineNoRows.length > 0) throw new Error(`Rows ${summary.missingLineNoRows.join(', ')} are missing ${summary.config.lineNoField}.`);
        setPendingUpdateData({ records, xmlSnippet: buildDocumentPreviewXml(queryObj, records, detectedHeadersRaw), isRateTable: false, isDocEntry: true, isTransRule: false, isItemNested: false, isContractLineEntry: false, isVendorBank: false, isAppPayment: false, documentSummary: summary, documentHeaders: detectedHeadersRaw });
      } else if (isAppPaymentCreate(queryObj, mode)) {
        const summary = getAppPaymentCreateSummary(records);
        if (summary.vendorConflicts.length > 0) throw new Error(`VENDORID mismatch — correct before continuing:\n${summary.vendorConflicts.join('\n')}`);
        if (summary.missingDocNoRows.length > 0) throw new Error(`Rows ${summary.missingDocNoRows.join(', ')} are missing DOCNUMBER.`);
        setPendingUpdateData({ records, xmlSnippet: buildAppPaymentPreviewXml(records), isRateTable: false, isDocEntry: false, isTransRule: false, isItemNested: false, isContractLineEntry: false, isVendorBank: false, isAppPayment: true, appPaymentSummary: summary });
      } else {
        const sampleXml = buildWritePreviewXml(queryObj, records, mode);
        setPendingUpdateData({ records, xmlSnippet: sampleXml, isRateTable: false, isDocEntry: false, isTransRule: false, isItemNested: false, isContractLineEntry: false, isVendorBank: false, isAppPayment: false });
      }
      setIsUpdateModalOpen(true);
    } catch (err) { setStatus(`Error: ${err.message}`); } finally { setLoading(false); }
  };

  const confirmWrite = async () => {
    if (!pendingUpdateData) return;
    setIsUpdateModalOpen(false); setLoading(true);
    const { records, isRateTable, isDocEntry, isTransRule, isItemNested, isContractLineEntry, isVendorBank, isAppPayment, rateTableSummary, documentSummary, transRuleSummary, itemNestedSummary, contractLineSummary, vendorBankSummary, appPaymentSummary, documentHeaders } = pendingUpdateData;
    const totalRecords = records.length;
    
    if (isRateTable) setStatus(`${mode === 'create' ? 'Creating' : 'Updating'} ${rateTableSummary?.totalRateTables || 1} rate table(s)...`);
    else if (isItemNested) setStatus(`Updating ${itemNestedSummary?.totalItems || 1} item(s) with ${totalRecords} ${queryObj} entries...`);
    else if (isContractLineEntry) setStatus(`Updating ${contractLineSummary?.totalParentLines || 1} contract line(s) with ${totalRecords} entries...`);
    else if (isTransRule) setStatus(`Creating ${transRuleSummary?.totalRules || 1} transaction rule(s)...`);
    else if (isVendorBank) setStatus(`Creating bank details for ${vendorBankSummary?.totalVendors || 1} vendor(s)...`);
    else if (isDocEntry) setStatus(`Updating ${documentSummary?.totalDocuments || 1} document(s)...`);
    else if (isAppPayment) setStatus(`Creating ${appPaymentSummary?.totalPayments || 1} payment(s) from ${totalRecords} row(s)...`);
    else setStatus(`${mode === 'create' ? 'Creating' : 'Updating'} ${totalRecords} records...`);
    
    try {
      if (isTestMode) { await new Promise(r => setTimeout(r, 1000)); setStatus("Success! (Test Mode)"); }
      else if (isRateTable) {
        const results = mode === 'create' ? await createRateTableEntries(config, queryObj, records) : await writeRateTableEntries(config, queryObj, records);
        await writeGroupedResultsToExcel(results, records, 'RATETABLEID');
        const successCount = results?.filter(r => r.status === 'success').length || 0;
        const totalTables = rateTableSummary?.totalRateTables || results?.length || 0;
        setStatus(successCount === totalTables ? `Success! ${mode === 'create' ? 'Created' : 'Updated'} ${successCount} rate table(s).` : `Completed. Success: ${successCount}/${totalTables}`);
      } else if (isItemNested) {
        const results = await writeItemNestedEntries(config, queryObj, records);
        await writeGroupedResultsToExcel(results, records, itemNestedSummary?.config?.parentKeyField || 'ITEMID');
        const successCount = results?.filter(r => r.status === 'success').length || 0;
        const totalItems = itemNestedSummary?.totalItems || results?.length || 0;
        setStatus(successCount === totalItems ? `Success! Updated ${successCount} item(s).` : `Completed. Success: ${successCount}/${totalItems}`);
      } else if (isContractLineEntry) {
        const parentKey = contractLineSummary?.parentKeyHeader;
        const results = await writeContractLineEntries(config, queryObj, records, parentKey);
        await writeGroupedResultsToExcel(results, records, parentKey);
        const successCount = results?.filter(r => r.status === 'success').length || 0;
        const totalLines = contractLineSummary?.totalParentLines || results?.length || 0;
        setStatus(successCount === totalLines ? `Success! Updated ${successCount} contract line(s).` : `Completed. Success: ${successCount}/${totalLines}`);
      } else if (isTransRule) {
        const results = await createTransactionRules(config, records);
        await writeGroupedResultsToExcel(results, records, 'NAME');
        const successCount = results?.filter(r => r.status === 'success').length || 0;
        const totalRules = transRuleSummary?.totalRules || results?.length || 0;
        setStatus(successCount === totalRules ? `Success! Created ${successCount} transaction rule(s).` : `Completed. Success: ${successCount}/${totalRules}`);
      } else if (isVendorBank) {
        const results = await writeVendorBankDetails(config, records);
        await writeGroupedResultsToExcel(results, records, 'VENDORID');
        const successCount = results?.filter(r => r.status === 'success').length || 0;
        const totalVendors = vendorBankSummary?.totalVendors || results?.length || 0;
        setStatus(successCount === totalVendors ? `Success! Created bank details for ${successCount} vendor(s).` : `Completed. Success: ${successCount}/${totalVendors}`);
      } else if (isDocEntry) {
        const results = await writeDocumentEntries(config, queryObj, records, documentHeaders);
        await writeGroupedResultsToExcel(results, records, documentSummary?.keyHeader || documentSummary?.config?.parentKeyField || 'DOCUMENTID');
        const successCount = results?.filter(r => r.status === 'success').length || 0;
        setStatus(successCount === documentSummary?.totalDocuments ? `Success! Updated ${successCount} document(s).` : `Completed. Success: ${successCount}/${documentSummary?.totalDocuments}`);
      } else if (isAppPayment) {
        const results = await createAppPayments(config, records);
        await writeGroupedResultsToExcel(results, records, 'DOCNUMBER');
        const successCount = results?.filter(r => r.status === 'success').length || 0;
        const totalPayments = appPaymentSummary?.totalPayments || results?.length || 0;
        setStatus(successCount === totalPayments ? `Success! Created ${successCount} payment(s).` : `Completed. Success: ${successCount}/${totalPayments}`);
      } else {
        const results = await writeIntacctRecords(config, queryObj, records, mode, (p, t) => setStatus(`${mode === 'create' ? 'Creating' : 'Updating'} ${p} / ${t}...`));
        await writeResultsToExcel(results);
        const successCount = results.filter(r => r.status === 'success').length;
        setStatus(successCount === results.length ? `Success! All ${successCount} records processed.` : `Completed. Success: ${successCount}, Failed: ${results.length - successCount}`);
      }
    } catch (err) { setStatus(`Failed: ${err.message}`); } finally { setLoading(false); setPendingUpdateData(null); }
  };

  // ============ JOB COST HANDLER ============
  const confirmJobCostSetup = async () => {
    if (!jobCostData) return;
    setJobCostModalOpen(false); setLoading(true);
    const { records, summary } = jobCostData;
    try {
      if (isTestMode) {
        setJobCostResults({ tasks: summary.tasks.map(t => ({ ...t, status: 'success', message: 'Success (Test)' })), costTypes: summary.costTypes.map(ct => ({ ...ct, status: 'success', message: 'Success (Test)' })), summary: { tasksAttempted: summary.totalTasks, tasksSucceeded: summary.totalTasks, tasksRetried: 0, tasksRetriedSucceeded: 0, tasksFailed: 0, costTypesAttempted: summary.totalCostTypes, costTypesSucceeded: summary.totalCostTypes, costTypesFailed: 0 } });
        setJobCostModalOpen(true); setStatus("Success! (Test Mode)");
      } else {
        const results = await executeJobCostSetup(config, records, (step, cur, tot) => setStatus(`${step === 'tasks-standard' ? 'Creating tasks...' : step === 'tasks-retry' ? 'Retrying tasks...' : 'Creating cost types...'} ${cur}/${tot}`));
        setJobCostResults(results); setJobCostModalOpen(true);
        const allSuccess = results.summary.tasksFailed === 0 && results.summary.costTypesFailed === 0;
        setStatus(allSuccess ? `Success! Created ${results.summary.tasksSucceeded + results.summary.tasksRetriedSucceeded} task(s) and ${results.summary.costTypesSucceeded} cost type(s).` : `Completed with errors.`);
      }
    } catch (err) { setStatus(`Failed: ${err.message}`); } finally { setLoading(false); }
  };

  // ============ COST STRUCTURE HANDLERS ============
  const handleValidateCostStructure = async () => {
    setLoading(true);
    setStatus('Reading selection for cost structure validation...');
    setCostStructureTaskResults(null);
    setCostStructureCostTypeResults(null);
    try {
      let rawHeaders = [];
      let dataRows = [];
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange().getIntersectionOrNullObject(context.workbook.worksheets.getActiveWorksheet().getUsedRange());
        range.load('values, isNullObject');
        await context.sync();
        if (range.isNullObject || !range.values || range.values.length < 2) throw new Error('Select headers AND data rows.');
        rawHeaders = range.values[0];
        dataRows = range.values.slice(1);
      });

      const { mapping, error } = detectCostStructureHeaders(rawHeaders);
      if (error) throw new Error(error);

      const rows = extractCostStructureRows(mapping, dataRows);
      if (rows.length === 0) throw new Error('No valid rows found (each row needs Project, Cost Code, and Cost Type).');

      setStatus(`Validating ${rows.length} combination(s) against Intacct...`);
      const validation = await validateCostStructure(config, rows);

      setCostStructureRows(rows);
      setCostStructureValidation(validation);
      setCostStructureModalOpen(true);
      setStatus(`Found ${validation.found}/${validation.totalRequired}. Missing ${validation.missingTasks.length} task(s), ${validation.missingCostTypes.length} cost type(s).`);
    } catch (err) {
      setStatus(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCostStructureTasks = async () => {
    if (!costStructureValidation || !costStructureRows) return;
    setCostStructureBusyAction('tasks');
    try {
      setStatus(`Creating ${costStructureValidation.missingTasks.length} task(s)...`);
      const results = await createCostStructureTasks(config, costStructureValidation.missingTasks);
      setCostStructureTaskResults(results);

      setStatus('Re-validating cost structure...');
      const newValidation = await validateCostStructure(config, costStructureRows);
      setCostStructureValidation(newValidation);

      const created = results.filter(r => r.status === 'created').length;
      const errored = results.filter(r => r.status === 'error').length;
      setStatus(`Tasks: ${created} created, ${errored} error(s). ${newValidation.missingCostTypes.length} cost type(s) still missing.`);
    } catch (err) {
      setStatus(`Task creation failed: ${err.message}`);
    } finally {
      setCostStructureBusyAction(null);
    }
  };

  const handleCreateCostStructureCostTypes = async () => {
    if (!costStructureValidation || !costStructureRows) return;
    setCostStructureBusyAction('costtypes');
    try {
      setStatus(`Creating ${costStructureValidation.missingCostTypes.length} cost type(s)...`);
      const results = await createCostStructureCostTypes(config, costStructureValidation.missingCostTypes);
      setCostStructureCostTypeResults(results);

      setStatus('Re-validating cost structure...');
      const newValidation = await validateCostStructure(config, costStructureRows);
      setCostStructureValidation(newValidation);

      const created = results.filter(r => r.status === 'created').length;
      const errored = results.filter(r => r.status === 'error').length;
      if (newValidation.missingTasks.length === 0 && newValidation.missingCostTypes.length === 0) {
        setStatus(`Cost types: ${created} created, ${errored} error(s). Cost structure fully validated.`);
      } else {
        setStatus(`Cost types: ${created} created, ${errored} error(s). Still missing ${newValidation.missingTasks.length} task(s), ${newValidation.missingCostTypes.length} cost type(s).`);
      }
    } catch (err) {
      setStatus(`Cost type creation failed: ${err.message}`);
    } finally {
      setCostStructureBusyAction(null);
    }
  };

  const closeCostStructureModal = () => {
    setCostStructureModalOpen(false);
    setCostStructureValidation(null);
    setCostStructureRows(null);
    setCostStructureTaskResults(null);
    setCostStructureCostTypeResults(null);
  };

  // ============ ATTACHMENT HANDLERS ============
  const linkAttachmentExcelRange = async () => { try { await Excel.run(async (context) => { const range = context.workbook.getSelectedRange(); range.load("address"); await context.sync(); setAttachmentExcelRef(range.address); setAttachmentSource('excel'); }); } catch (err) { setStatus(`Error: ${err.message}`); } };
  const handleBrowseFolder = async () => { const path = await browseFolderPath(); if (path) setSavePath(path); };
  
  // ============ GET MODE HANDLERS ============
  const linkGetExcelRange = async () => { 
    try { 
      await Excel.run(async (context) => { 
        const range = context.workbook.getSelectedRange(); 
        range.load("address"); 
        await context.sync(); 
        setGetExcelRef(range.address); 
      }); 
    } catch (err) { 
      setStatus(`Error: ${err.message}`); 
    } 
  };
  
  const resolveGetKeys = async () => {
    if (!getExcelRef) throw new Error('No Excel range selected for GET keys.');
    let keys = [];
    await Excel.run(async (context) => {
      const [sheetName, rangeAddr] = [getExcelRef.split('!')[0].replace(/'/g, ''), getExcelRef.split('!')[1]];
      const range = context.workbook.worksheets.getItem(sheetName).getRange(rangeAddr);
      range.load("values, rowIndex"); 
      await context.sync();
      let vals = range.values || []; 
      // Skip header if selection starts at row 0 and has multiple rows
      if (range.rowIndex === 0 && vals.length > 1) vals = vals.slice(1);
      keys = vals.flat().filter(v => v !== "" && v != null).map(v => String(v).trim());
    });
    return keys;
  };
  
  // Helper to pretty print XML
  const prettyPrintXml = (xmlString) => {
    try {
      // Parse and re-serialize with formatting
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'application/xml');
      
      // Check for parse errors
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        // If parsing fails, do simple regex-based formatting
        return xmlString
          .replace(/></g, '>\n<')
          .replace(/(<[^/][^>]*>)([^<]+)(<\/)/g, '$1$2$3')
          .split('\n')
          .map((line, i, arr) => {
            // Simple indentation based on tag depth
            const openTags = (line.match(/<[^/!?][^>]*[^/]>/g) || []).length;
            const closeTags = (line.match(/<\/[^>]+>/g) || []).length;
            return line.trim();
          })
          .join('\n');
      }
      
      // Use XSLT for pretty printing
      const xsltDoc = parser.parseFromString(`
        <xsl:stylesheet version="1.0" xmlns:xsl="http://www.w3.org/1999/XSL/Transform">
          <xsl:output method="xml" indent="yes" omit-xml-declaration="no"/>
          <xsl:strip-space elements="*"/>
          <xsl:template match="node()|@*">
            <xsl:copy>
              <xsl:apply-templates select="node()|@*"/>
            </xsl:copy>
          </xsl:template>
        </xsl:stylesheet>
      `, 'application/xml');
      
      const xsltProcessor = new XSLTProcessor();
      xsltProcessor.importStylesheet(xsltDoc);
      const resultDoc = xsltProcessor.transformToDocument(xmlDoc);
      const serializer = new XMLSerializer();
      return serializer.serializeToString(resultDoc);
    } catch (e) {
      // Fallback: simple regex-based formatting
      let formatted = xmlString.replace(/></g, '>\n<');
      let indent = 0;
      const lines = formatted.split('\n');
      return lines.map(line => {
        line = line.trim();
        if (!line) return '';
        
        // Decrease indent for closing tags
        if (line.match(/^<\//) || line.match(/\/>/)) {
          indent = Math.max(0, indent - 1);
        }
        
        const padding = '  '.repeat(indent);
        
        // Increase indent for opening tags (not self-closing)
        if (line.match(/^<[^/!?]/) && !line.match(/\/>$/) && !line.match(/<\/[^>]+>$/)) {
          indent++;
        }
        
        return padding + line;
      }).join('\n');
    }
  };

  // Helper to download XML as a file
  const downloadXmlFile = (xmlContent, filename, prettify = true) => {
    const content = prettify ? prettyPrintXml(xmlContent) : xmlContent;
    const blob = new Blob([content], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // Build GET request XML (for export)
  const buildGetRequestXml = (objectName, keys, keyType) => {
    const functionTag = keyType === 'recordno' ? 'read' : 'readByName';
    const keysString = keys.map(k => escapeXml(String(k))).join(',');
    
    let authXml = config.useSessionId && config.sessionId 
      ? `<sessionid>${config.sessionId}</sessionid>` 
      : `<login>
      <userid>${config.userId}</userid>
      <companyid>${config.companyId}</companyid>
      <password>${config.userPassword}</password>${config.locationId ? `
      <locationid>${config.locationId}</locationid>` : ''}
    </login>`;
    
    return `<?xml version="1.0" encoding="UTF-8"?>
<request>
  <control>
    <senderid>${config.senderId}</senderid>
    <password>${config.senderPassword}</password>
    <controlid>${new Date().getTime()}</controlid>
    <uniqueid>false</uniqueid>
    <dtdversion>3.0</dtdversion>
    <includewhitespace>false</includewhitespace>
  </control>
  <operation>
    <authentication>
      ${authXml}
    </authentication>
    <content>
      <function controlid="get-request">
        <${functionTag}>
          <object>${objectName}</object>
          <keys>${keysString}</keys>
          <fields>*</fields>
        </${functionTag}>
      </function>
    </content>
  </operation>
</request>`;
  };
  
  const resolveAttachmentIds = async () => {
    if (attachmentSource === 'manual') return attachmentIds.split(/[,\n]/).map(id => id.trim()).filter(Boolean);
    let ids = [];
    await Excel.run(async (context) => {
      const [sheetName, rangeAddr] = [attachmentExcelRef.split('!')[0].replace(/'/g, ''), attachmentExcelRef.split('!')[1]];
      const range = context.workbook.worksheets.getItem(sheetName).getRange(rangeAddr);
      range.load("values, rowIndex"); await context.sync();
      let vals = range.values || []; if (range.rowIndex === 0 && vals.length > 1) vals = vals.slice(1);
      ids = vals.flat().filter(v => v !== "" && v != null).map(v => String(v).trim());
    });
    return ids;
  };
  
  const handleDownloadAttachments = async () => {
    if (!savePath) { setStatus('Error: Please specify a save location'); return; }
    setLoading(true); setStatus('Resolving attachment IDs...'); setAttachmentResults([]);
    try {
      const ids = await resolveAttachmentIds(); if (ids.length === 0) { setStatus('Error: No attachment IDs found'); setLoading(false); return; }
      setStatus(`Downloading ${ids.length} attachment(s)...`);
      const result = await downloadAttachmentsBatch(config, ids, savePath);
      setAttachmentResults(result.results);
      setStatus(result.summary.errors === 0 ? `Success! Downloaded ${result.summary.success} attachment(s)` : `Completed. Success: ${result.summary.success}, Failed: ${result.summary.errors}`);
    } catch (err) { setStatus(`Error: ${err.message}`); } finally { setLoading(false); }
  };

  // ============ REFRESH HANDLER ============
  const handleRefreshWorkbook = async () => {
    setLoading(true); setStatus('Scanning workbook...');
    try {
      let sheetsProcessed = 0;
      await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets; sheets.load("items/name"); await context.sync();
        for (let sheet of sheets.items) {
          const settingsKey = sheet.customProperties.getItemOrNullObject("IntacctConfig"); await context.sync();
          if (!settingsKey.isNullObject) {
            settingsKey.load("value"); await context.sync();
            const savedConfig = JSON.parse(settingsKey.value);
            setStatus(`Refreshing ${sheet.name}...`);
            const fieldsString = Array.isArray(savedConfig.fields) ? savedConfig.fields.join(',') : savedConfig.fields;
            const resolvedFilter = await resolveDynamicFilters(savedConfig.query);
            let data = isTestMode ? [{ RECORDNO: "999", TITLE: "Refreshed" }] : [];
            if (!isTestMode) {
              // Check if this sheet used legacy query mode
              if (savedConfig.useLegacyQuery) {
                const response = await fetchIntacctDataLegacy(config, savedConfig.object, fieldsString, savedConfig.legacyQueryString || '', 1000, true);
                data = response.data || [];
              } else {
                let response = await fetchIntacctData(config, savedConfig.object, fieldsString, resolvedFilter, 2000, 0, savedConfig.showPrivate || false, savedConfig.orderBy || null);
                data = response.data || []; const totalCount = response.metadata?.totalcount || data.length; let currentOffset = data.length;
                while (currentOffset < totalCount) { const moreRes = await fetchMoreIntacctData(config, savedConfig.object, fieldsString, resolvedFilter, 2000, currentOffset, savedConfig.showPrivate || false, savedConfig.orderBy || null); if (moreRes.data?.length > 0) { data = [...data, ...moreRes.data]; currentOffset += moreRes.data.length; } else break; }
              }
            }
            if (data.length > 0) {
              const targetSheet = context.workbook.worksheets.getItem(sheet.name); targetSheet.getRange().clear();
              const keys = Array.isArray(savedConfig.fields) ? savedConfig.fields : savedConfig.fields.split(',');
              targetSheet.getRange("A1").getResizedRange(0, keys.length - 1).values = [keys];
              targetSheet.getRange("A1").getResizedRange(0, keys.length - 1).format.font.bold = true;
              targetSheet.getRange("A2").getResizedRange(data.length - 1, keys.length - 1).numberFormat = Array(data.length).fill(Array(keys.length).fill("@"));
              targetSheet.getRange("A2").getResizedRange(data.length - 1, keys.length - 1).values = data.map(row => keys.map(k => row[k] || ""));
              targetSheet.getUsedRange().format.autofitColumns();
            }
            sheetsProcessed++;
          }
        }
      });
      setStatus(`Refresh Complete. Updated ${sheetsProcessed} sheets.`);
    } catch (error) { setStatus(`Refresh Failed: ${error.message}`); } finally { setLoading(false); }
  };

  // ============ RENDER ============
  return (
    <div className="p-4 bg-gray-900 min-h-screen flex flex-col font-sans relative">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <LightningIcon className="w-7 h-7 text-yellow-400" />
          <h1 className="text-xl font-bold text-white">Dr.Lightning <span className="text-yellow-400">5000</span></h1>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-gray-800 rounded p-0.5 border border-gray-700">
            <button onClick={() => setMode('read')} className={`px-3 py-1 text-xs font-bold rounded ${mode === 'read' ? 'bg-gray-700 shadow text-blue-400' : 'text-gray-400 hover:text-gray-300'}`}>Read</button>
            <button onClick={() => setMode('update')} className={`px-3 py-1 text-xs font-bold rounded ${mode === 'update' ? 'bg-gray-700 shadow text-red-400' : 'text-gray-400 hover:text-gray-300'}`}>Update</button>
            <button onClick={() => setMode('create')} className={`px-3 py-1 text-xs font-bold rounded ${mode === 'create' ? 'bg-gray-700 shadow text-green-400' : 'text-gray-400 hover:text-gray-300'}`}>Create</button>
            <button onClick={() => setMode('purchasing')} className={`px-3 py-1 text-xs font-bold rounded flex items-center gap-1 ${mode === 'purchasing' ? 'bg-gray-700 shadow text-amber-400' : 'text-gray-400 hover:text-gray-300'}`}><ShoppingCartIcon /> PO</button>
            <button onClick={() => setMode('attachments')} className={`px-3 py-1 text-xs font-bold rounded flex items-center gap-1 ${mode === 'attachments' ? 'bg-gray-700 shadow text-purple-400' : 'text-gray-400 hover:text-gray-300'}`}><PaperclipIcon /> Files</button>
            <button onClick={() => setMode('tools')} className={`px-3 py-1 text-xs font-bold rounded flex items-center gap-1 ${mode === 'tools' ? 'bg-gray-700 shadow text-cyan-400' : 'text-gray-400 hover:text-gray-300'}`}><WrenchIcon /> Tools</button>
            <button onClick={() => setMode('reports')} className={`px-3 py-1 text-xs font-bold rounded flex items-center gap-1 ${mode === 'reports' ? 'bg-gray-700 shadow text-emerald-400' : 'text-gray-400 hover:text-gray-300'}`}>📊 Reports</button>
          <button 
  onClick={() => setMode('usage')} 
  className={`px-3 py-1 text-xs font-bold rounded flex items-center gap-1 ${
    mode === 'usage' ? 'bg-gray-700 shadow text-cyan-400' : 'text-gray-400 hover:text-gray-300'
  }`}
>
  ⚡ Usage
</button>
          </div>
          <button onClick={loadSettingsFromActiveSheet} className="text-xs flex items-center text-yellow-400 bg-gray-800 px-2 py-1 rounded hover:bg-gray-700 border border-gray-700"><DownloadIcon /> Load</button>
        </div>
      </div>

      {/* Connection Info */}
      <div className="bg-gray-800 p-3 rounded shadow-sm border border-gray-700 mb-4 flex justify-between items-center">
        <div>{config.useSessionId ? <div className="text-xs text-gray-400">Connected via <span className="font-bold text-white">Session ID</span></div> : <><div className="text-xs text-gray-400">Connected as <span className="font-bold text-white">{config.userId}</span></div><div className="text-xs text-gray-400">Company ID <span className="font-bold text-yellow-400">{config.companyId}</span></div></>}</div>
        <button onClick={() => setIsCredentialsModalOpen(true)} className="text-xs text-yellow-400 hover:text-yellow-300 font-medium border border-yellow-400/30 rounded px-2 py-1 bg-yellow-400/10">Edit</button>
      </div>

      {/* Mode Panels */}
      {mode === 'read' && (
        <ReadModePanel 
          category={category} 
          setCategory={setCategory} 
          queryObj={queryObj} 
          onObjectChange={handleObjectChange} 
          filteredObjects={filteredObjects} 
          selectedFields={selectedFields} 
          onOpenFieldPicker={() => setIsFieldPickerOpen(true)} 
          useLabelHeaders={useLabelHeaders} 
          setUseLabelHeaders={setUseLabelHeaders} 
          sqlFilter={sqlFilter} 
          onOpenFilterBuilder={handleOpenFilterBuilder} 
          isInspecting={isInspecting}
          // Legacy Query Mode props
          useLegacyQuery={useLegacyQuery}
          setUseLegacyQuery={setUseLegacyQuery}
          legacyQueryString={legacyQueryString}
          setLegacyQueryString={setLegacyQueryString}
          // GET Mode props
          useGetMode={useGetMode}
          setUseGetMode={setUseGetMode}
          getExcelRef={getExcelRef}
          onLinkGetExcelRange={linkGetExcelRange}
          getKeyType={getKeyType}
          setGetKeyType={setGetKeyType}
          exportGetXml={exportGetXml}
          setExportGetXml={setExportGetXml}
        />
      )}
      {(mode === 'update' || mode === 'create') && <WriteModePanel mode={mode} queryObj={queryObj} onObjectChange={handleObjectChange} filteredObjects={filteredObjects} loading={loading} onPreviewClick={handleWriteClick} costStructureMode={costStructureMode} onCostStructureModeChange={setCostStructureMode} onValidateCostStructure={handleValidateCostStructure} />}
      {mode === 'purchasing' && <PurchasingModePanel poFilter={poFilter} onOpenPoFilterBuilder={handleOpenPoFilterBuilder} loading={loading} isInspecting={isInspecting} onLoadPOLines={handleLoadPOLines} onPreviewPOCreate={handlePreviewPOCreate} />}
      {mode === 'attachments' && <AttachmentsModePanel attachmentSource={attachmentSource} setAttachmentSource={setAttachmentSource} attachmentIds={attachmentIds} setAttachmentIds={setAttachmentIds} attachmentExcelRef={attachmentExcelRef} onLinkExcelRange={linkAttachmentExcelRange} savePath={savePath} setSavePath={setSavePath} onBrowseFolder={handleBrowseFolder} loading={loading} onDownload={handleDownloadAttachments} attachmentResults={attachmentResults} />}
      {mode === 'tools' && <ToolsModePanel loading={loading} setStatus={setStatus} />}
      {mode === 'reports' && <ReportsModePanel config={config} loading={loading} setLoading={setLoading} setStatus={setStatus} isTestMode={isTestMode} />}
      {mode === 'usage' && <ApiUsagePanel config={config} />}

      {/* Footer */}
      <div className="mt-auto space-y-2 sticky bottom-0 bg-gray-900 pt-2 pb-4">
        <div className="mb-2 flex items-center justify-end"><label className="flex items-center space-x-2 text-xs text-gray-400 cursor-pointer"><input type="checkbox" checked={isTestMode} onChange={(e) => setIsTestMode(e.target.checked)} className="rounded text-yellow-400 bg-gray-700 border-gray-600" /><span>Test Mode</span></label></div>
        {!pendingDownload && mode === 'read' && (<><div className="grid grid-cols-4 gap-2"><button onClick={handleRunReport} disabled={loading || isInspecting} className={`col-span-3 py-3 px-4 rounded-md shadow-sm text-sm font-bold text-gray-900 ${loading ? 'bg-gray-600 cursor-wait' : 'bg-yellow-400 hover:bg-yellow-300'}`}>{loading ? 'Processing...' : '⚡ Run Report (Save)'}</button><button onClick={generatePreviewXml} title="View XML Payload" className="col-span-1 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-md flex items-center justify-center border border-gray-600"><CodeIcon /></button></div><button onClick={handleRefreshWorkbook} disabled={loading} className="w-full py-2 px-4 bg-gray-800 border border-green-500/50 text-green-400 rounded-md text-sm font-semibold hover:bg-green-500/10 flex items-center justify-center"><RefreshIcon /> Refresh Workbook</button></>)}
        {pendingDownload && (<div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded text-center"><div className="text-sm font-bold text-yellow-400 mb-2">Large Dataset Found</div><div className="text-xs text-yellow-300/80 mb-3">Found {pendingDownload.totalCount.toLocaleString()} records.</div><div className="flex gap-2"><button onClick={cancelDownload} className="flex-1 py-2 px-2 bg-gray-800 border border-gray-600 rounded text-xs font-medium text-gray-300 hover:bg-gray-700">Cancel</button><button onClick={confirmDownload} className="flex-1 py-2 px-2 bg-yellow-500 text-gray-900 rounded text-xs font-medium hover:bg-yellow-400">Download All</button></div></div>)}
        {status && <div className={`mt-3 p-3 rounded text-xs border ${status.includes('Success') ? 'bg-green-500/10 text-green-400 border-green-500/30' : status.includes('Error') || status.includes('Failed') ? 'bg-red-500/10 text-red-400 border-red-500/30' : 'bg-blue-500/10 text-blue-400 border-blue-500/30'}`}>{status}</div>}
      </div>

      {/* Modals */}
      <FieldPickerModal isOpen={isFieldPickerOpen} onClose={() => setIsFieldPickerOpen(false)} available={availableFields} selected={selectedFields} onChange={setSelectedFields} />
      <FilterBuilderModal isOpen={isFilterBuilderOpen} onClose={handleCloseFilterBuilder} fields={availableFields} currentFilter={sqlFilter} onApply={handleFilterApply} originalSheetName={filterOriginSheet.current} currentShowPrivate={showPrivate} currentOrderBy={orderBy} />
      <FilterBuilderModal isOpen={isPoFilterBuilderOpen} onClose={handleClosePoFilterBuilder} fields={poAvailableFields} currentFilter={poFilter} onApply={handlePoFilterApply} originalSheetName={filterOriginSheet.current} currentShowPrivate={poShowPrivate} currentOrderBy={poOrderBy} />
      <CredentialsModal isOpen={isCredentialsModalOpen} onClose={() => setIsCredentialsModalOpen(false)} config={config} onSave={setConfig} />
      <XmlPreviewModal isOpen={isXmlPreviewOpen} onClose={() => setIsXmlPreviewOpen(false)} xmlContent={xmlContent} />
      <UpdateConfirmationModal isOpen={isUpdateModalOpen} onClose={() => setIsUpdateModalOpen(false)} onConfirm={confirmWrite} recordCount={pendingUpdateData?.records?.length || 0} objectName={queryObj} xmlSnippet={pendingUpdateData?.xmlSnippet || ''} mode={mode} isRateTable={pendingUpdateData?.isRateTable || false} rateTableSummary={pendingUpdateData?.rateTableSummary || null} isDocEntry={pendingUpdateData?.isDocEntry || false} documentSummary={pendingUpdateData?.documentSummary || null} isTransRule={pendingUpdateData?.isTransRule || false} transRuleSummary={pendingUpdateData?.transRuleSummary || null} isContractLineEntry={pendingUpdateData?.isContractLineEntry || false} contractLineSummary={pendingUpdateData?.contractLineSummary || null} isVendorBank={pendingUpdateData?.isVendorBank || false} vendorBankSummary={pendingUpdateData?.vendorBankSummary || null} />
      <POCreateModal isOpen={poCreateModalOpen} onClose={() => { setPoCreateModalOpen(false); setPoCreateData(null); }} onConfirm={confirmPOCreate} createData={poCreateData} excludedFields={poExcludedFields} onExcludedFieldsChange={handleExcludedFieldsChange} />
      <JobCostSetupModal isOpen={jobCostModalOpen} onClose={() => { setJobCostModalOpen(false); setJobCostData(null); setJobCostResults(null); }} onConfirm={confirmJobCostSetup} jobCostData={jobCostData} jobCostResults={jobCostResults} />
      <CostStructureModal isOpen={costStructureModalOpen} onClose={closeCostStructureModal} validation={costStructureValidation} taskResults={costStructureTaskResults} costTypeResults={costStructureCostTypeResults} busyAction={costStructureBusyAction} onCreateTasks={handleCreateCostStructureTasks} onCreateCostTypes={handleCreateCostStructureCostTypes} />
      
      {/* Vendor Bank File Detail - Header Suggestion Modal */}
      {vendorBankHeadersModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-gray-800 rounded-lg shadow-xl border border-teal-500/30 p-6 mx-4 max-w-md">
            <div className="flex items-center gap-2 mb-4 text-teal-400">
              <LightningIcon className="w-5 h-5" />
              <span className="font-bold text-sm">VENDORBANKFILEDETAIL</span>
            </div>
            <p className="text-sm text-gray-300 mb-2">
              The active sheet appears to be blank. Would you like to add the suggested headers for creating vendor bank file details?
            </p>
            <div className="bg-gray-900 rounded p-3 mb-4 text-xs text-teal-300 font-mono overflow-x-auto">
              {getVendorBankSuggestedHeaders().join(' | ')}
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Fill in your data below the headers, then click Preview Create again.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setVendorBankHeadersModalOpen(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-sm">
                Cancel
              </button>
              <button onClick={writeVendorBankHeaders} className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded font-medium text-sm">
                Add Headers
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;